import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Maximize2, Minimize2 } from 'react-feather';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import type { BrandInsightsPageContext, ChatContextItem } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { DispatchPageContextDataContext } from '@trinity/chat-components/components/Chat/context.js';
import { DigitalTwinChat } from '@trinity/chat-components/components/DigitalTwin/index.js';
import { SegmentAvatar } from '@trinity/chat-components/components/SegmentAvatar/index.js';
import { ChatInterfaceNative } from './components/ChatInterfaceNative';
import { ChatToggleBtn, LightbulbIcon } from './components/ChatPanel';
import { MessageGeneratorScreen } from '../MessageGenerator/MessageGeneratorScreen';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';
import { FileIcon } from '@trinity/chat-components/components/icons/File/index.js';
import { PageRenderer } from '@trinity/chat-components/components/PageDashboard/PageRenderer/index.js';
import {
  SegmentPageBuilderContainer,
  SegmentPageBuilderApiProvider,
} from '@trinity/chat-components/containers/SegmentPageBuilder/index.js';
import type { SegmentPageBuilderApi } from '@trinity/chat-components/containers/SegmentPageBuilder/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import { usePage } from '@trinity/chat-components/hooks/usePage.js';
import chatBackgroundImg from '@trinity/chat-components/static/images/chat-background.png';
import {
  createSegmentRecord,
  getProjectFiles,
  getSegmentByPage,
  getSegmentationRun,
  getSegmentPersonaStatus,
  getSegmentQuoteStatus,
  listSegments,
  patchSegmentMetadata,
  type ProjectFileItem,
  type SegmentationRunRecord,
} from './api/client';
import SegmentMarketResearchInsights, {
  type SegmentQuotesData,
} from './components/SegmentMarketResearchInsights';
import SegmentMarketViewPanel, { type CharacteristicsSection } from './components/SegmentMarketViewPanel';
import SegmentOverviewCard from './components/SegmentOverviewCard';
import { getSegmentAvatar } from './constants/avatars';
import { useForceHackathonBrand } from './hooks/useForceHackathonBrand';
import useSessionStore from './state/sessionStore';
import { buildSegmentDashboard } from './utils/buildSegmentDashboard';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SegmentData {
  id: number;
  name: string;
  size: number;
  pct: number;
  profile_narrative?: string;
  commercial_action?: string;
  gap_analysis?: string | Record<string, unknown>;
  key_stats?: Record<string, Record<string, unknown>>;
  distinguishing_features?: { name: string; z_score: number }[];
  top_molecules?: { molecule: string; mean_share: number }[];
  archetype_distribution?: Record<string, number>;
  momentum_distribution?: Record<string, number>;
  _projectBrand?: string;
  _projectTitle?: string;
}

// ─── Section ordering types ──────────────────────────────────────────────────
type SectionKey = 'overview' | 'characteristics' | 'market_research_insights' | 'custom_dashboard';
interface SectionOrderItem {
  key: SectionKey;
  hidden: boolean;
}
const DEFAULT_SECTION_ORDER: SectionOrderItem[] = [
  { key: 'overview', hidden: false },
  { key: 'characteristics', hidden: false },
  { key: 'market_research_insights', hidden: false },
  { key: 'custom_dashboard', hidden: false },
];
const SECTION_LABELS: Record<SectionKey, string> = {
  overview: 'Segment Overview',
  characteristics: 'HCP Characteristics',
  market_research_insights: 'Market Research Insights',
  custom_dashboard: 'Custom Dashboard',
};

interface LocationState {
  segment?: SegmentData;
  segmentIndex?: number;
  // Optional fast-path: ExploreSegmentsPage passes the raw DB row for this
  // page through navigation state so this screen can render immediately
  // without re-fetching listSegments just to find the one matching row.
  // The record may be SKINNY (metadata._listView === true) because
  // Explore uses view=list for bulk loading — in that case the hero/
  // narrative still paint from state but we upgrade to the fat row via
  // GET /segments/by-page/:pageId in the background.
  segmentDbRecord?: Record<string, unknown>;
}

function basenameOf(p: string): string {
  const cleaned = p.replace(/\\/g, '/');
  const idx = cleaned.lastIndexOf('/');
  return idx >= 0 ? cleaned.slice(idx + 1) : cleaned;
}

function extensionOf(name: string): string {
  const base = basenameOf(name);
  const idx = base.lastIndexOf('.');
  if (idx < 0 || idx === base.length - 1) return '';
  return base.slice(idx + 1).toLowerCase();
}

// True when the record came from the skinny view=list serializer and
// doesn't contain the fat metadata (marketView, qualInsights, quotes,
// full segmentProfile). Marker is set in serializeSegmentListView on the
// chat-api side. Absent marker ⇒ fat row (or nothing at all).
function isSkinnyRecord(record: Record<string, unknown> | null | undefined): boolean {
  if (!record) return false;
  const meta = record.metadata as Record<string, unknown> | undefined;
  return meta?._listView === true;
}

// ─── Migrate Button (temporary) ──────────────────────────────────────────────

function MigrateToDbButton({
  seg,
  pageId,
  pageData,
}: {
  seg: SegmentData | null;
  pageId: number;
  pageData: unknown;
}): React.JSX.Element | null {
  const [migrating, setMigrating] = useState(false);
  const [migrated, setMigrated] = useState(false);

  if (!seg || migrated) return migrated ? <MigrateBadge>Migrated</MigrateBadge> : null;

  const handleMigrate = async (): Promise<void> => {
    setMigrating(true);
    try {
      const stored = pageData as Record<string, unknown> | undefined;
      const providerData = (stored as { providerData?: Record<string, unknown> })?.providerData;
      const segProfile = (providerData?.segmentProfile ?? seg) as Record<string, unknown>;

      const config = buildSegmentDashboard(segProfile as never, 'Segment Insights');

      await createSegmentRecord({
        projectId: 1,
        name: seg.name || `Segment ${pageId}`,
        description: seg.profile_narrative?.slice(0, 500) ?? undefined,
        iconUrl: getSegmentAvatar(pageId % 9),
        ttsSettings: { avatarId: undefined, ttsConfig: null },
        metadata: {
          _hackathon: true,
          _migratedFromPageId: pageId,
          segmentProfile: segProfile,
          subtitle: `${(seg.size ?? 0).toLocaleString()} HCPs (${(((seg.pct as number) ?? 0) * 100).toFixed(0)}%)`,
          attributes: seg.archetype_distribution
            ? Object.entries(seg.archetype_distribution)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 2)
                .map(([k]) => k)
            : undefined,
          sampleData: {
            segmentN: seg.size ?? 0,
            totalN: Math.round((seg.size ?? 0) / ((seg.pct as number) || 1)),
          },
        },
        dashboardConfig: config as unknown as Record<string, unknown>,
      });

      setMigrated(true);
      alert('Migrated to segments DB! Check /segments page.');
    } catch (err) {
      alert(`Migration failed: ${(err as Error)?.message ?? 'Unknown error'}`);
    } finally {
      setMigrating(false);
    }
  };

  return (
    <MigrateBtn onClick={handleMigrate} disabled={migrating}>
      {migrating ? 'Migrating...' : 'Migrate to Segments DB'}
    </MigrateBtn>
  );
}

const MigrateBtn = styled.button`
  margin-left: auto;
  background: #28aae1;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #1d94c7;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MigrateBadge = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: #4cb847;
  font-weight: 600;
`;

// ─── Styled (matching frontend SegmentDashboards styling) ────────────────────

const Root = styled.div`
  height: calc(100vh - 60px);
  background: url(${chatBackgroundImg}) no-repeat center;
  background-size: cover;
  display: flex;
  gap: 12px;
  overflow: hidden;
  padding: 5px;
`;

const LeftModule = styled.div<{ $chatOpen: boolean }>`
  width: ${(props) =>
    props.$chatOpen
      ? 'calc(100% - 437px)'
      : '100%'}; /* Full width when chat closed, otherwise viewport - sidebar(425px) - gap(12px) */
  max-width: 1280px;
  margin: 0 auto;
  background: #ffffff;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 12px;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  transition: width 0.3s ease;
`;

const RightModule = styled.div`
  width: 425px;
  flex-shrink: 0;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SidebarArea = styled.div`
  flex: 1;
  padding: 6px;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
`;

const Breadcrumbs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const BreadcrumbActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BackLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #626262;
  font-size: 14px;
  padding: 0;
  cursor: pointer;
  &:hover {
    color: #1271c4;
  }
`;

const ManageBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  color: #323232;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  padding: 6px 14px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  &:hover {
    background: #f8faff;
    border-color: #1271c4;
    color: #1271c4;
  }
`;

const EditModeBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${(p) => (p.$active ? '#f8faff' : '#ffffff')};
  border: 1px solid ${(p) => (p.$active ? '#1271c4' : '#d3d3d3')};
  border-radius: 4px;
  color: ${(p) => (p.$active ? '#1271c4' : '#323232')};
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  padding: 6px 14px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  &:hover {
    background: #f8faff;
    border-color: #1271c4;
    color: #1271c4;
  }
`;

/* Tab bar — single "Overview" tab, active state */
const TabBarWrap = styled.div`
  display: flex;
  position: relative;
  background-color: #ffffff;
`;

const Tab = styled.div<{ $active?: boolean; $clickable?: boolean }>`
  display: inline-flex;
  width: 150px;
  font-size: 16px;
  line-height: 45px;
  min-height: 45px;
  text-align: center;
  justify-content: center;
  font-weight: 700;
  color: ${(p) => (p.$active ? '#1271c4' : '#626262')};
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  position: relative;
  &:hover {
    color: ${(p) => (p.$clickable ? '#1271c4' : undefined)};
  }
`;

const TabIndicator = styled.div<{ $left: number }>`
  position: absolute;
  bottom: 0;
  left: ${(p) => p.$left}px;
  width: 150px;
  height: 4px;
  background-color: #1271c4;
  transition: left 0.15s ease;
`;

const TabBarLine = styled.div`
  height: 3px;
  background-color: #e5e5e5;
  width: 100%;
  position: absolute;
  bottom: 1px;
  left: 0;
  right: 0;
`;

/* Profile header card — matches frontend Header styled component */
const StickyHeaderArea = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #ffffff;
  padding-top: 5px;
  padding-bottom: 2px;
`;

const ProfileCard = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  color: #323232;
  padding: 16px;
  background: #ffffff;
  border-radius: 8px;
  margin-bottom: 5px;
  border: 1px solid #e5e5e5;
  overflow: visible;
  overflow: visible;
`;

const QuoteProcessingCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #f8f9fb;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
  color: #666;
  font-size: 14px;
`;

const QuoteProcessingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #e5e5e5;
  border-top-color: #4f46e5;
  border-radius: 50%;
  flex-shrink: 0;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/* Avatar — matches SegmentAvatar: 122px ring, 3px border */
const AvatarContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-left: 16px;
  margin-right: 24px;
  margin-bottom: 16px;
`;

const AvatarRing = styled.div`
  width: 122px;
  height: 122px;
  border-radius: 50%;
  border: 3px solid #28aae1;
  padding: 3px;
  background: white;
  box-sizing: border-box;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
`;

/* Left section with name, subtitle, tags — matches .leftSection */
const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProfileName = styled.div`
  color: #323232;
  font-size: 20px;
  font-weight: bold;
`;

const ProfileSubtitle = styled.div`
  color: #323232;
  font-size: 13px;
`;

const Tags = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Tag = styled.span`
  color: #323232;
  background-color: #e5f5fc;
  border-radius: 12px;
  padding: 6px 10px;
  font-size: 14px;
`;

/* Sample size box — matches SampleSize styled component */
const SampleSizeBox = styled.div`
  color: #323232;
  border-radius: 8px;
  flex-shrink: 0;
  background-color: #f2f2f2;
  padding: 16px;
  min-width: 280px;
  height: auto;
  font-size: 14px;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const SampleSizeValue = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const MetaBoxesRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const RightInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  margin-left: auto;
`;

const MetaBox = styled.div`
  color: #323232;
  border-radius: 8px;
  background-color: #f2f2f2;
  padding: 12px 16px;
  min-width: 100px;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaBoxLabel = styled.span`
  font-size: 12px;
  color: #626262;
`;

const MetaBoxValue = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #323232;
`;

const LockedTab = styled.div`
  display: inline-flex;
  width: 150px;
  font-size: 16px;
  line-height: 45px;
  min-height: 45px;
  text-align: center;
  justify-content: center;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #626262;
  cursor: not-allowed;
  opacity: 0.5;
  white-space: nowrap;
`;

/* Synthetic Audience tab — hosts the DigitalTwinChat component. The chat
   fills the available vertical space so the user can have a natural
   conversation with the segment's first-person persona. */
const SyntheticAudiencePanel = styled.div`
  margin: 8px 36px 16px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  min-height: calc(100vh - 420px);
  display: flex;
  flex-direction: column;

  /* Hide the chat disclaimer */
  [class*='DisclaimerContainer'] {
    display: none;
  }

  /* Hide the chat sidebar */
  [class*='SidebarWrapper'] {
    display: none;
  }
`;

const ExpandCollapseBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  color: #323232;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  padding: 6px 14px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  &:hover {
    background: #f8faff;
    border-color: #1271c4;
    color: #1271c4;
  }
`;

/* Files tab — grid of file cards matching Workspace.FilesTable/Files.Table.Grid */
const FilesPanel = styled.div`
  margin: 8px 36px 16px;
  padding: 20px;
  box-sizing: border-box;
`;

/* Message Generator tab — full height chat interface */
const MessageGeneratorPanel = styled.div`
  margin: 8px 36px 16px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  min-height: calc(100vh - 420px);
  display: flex;
  flex-direction: column;
`;

const MessageGeneratorTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 420px);
  overflow: hidden;
`;

const FilesSection = styled.div`
  & + & {
    margin-top: 28px;
  }
`;

const FilesSectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #626262;
  margin-bottom: 14px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

/* Files tab list-view rows */
const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
`;

const FileRow = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  cursor: pointer;
  box-sizing: border-box;
  border-bottom: 1px solid #f2f2f2;
  transition: background-color 0.12s ease;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8faff;
  }

  &:focus-visible {
    outline: 2px solid #1271c4;
    outline-offset: -2px;
  }
`;

const FileRowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
`;

const FileRowDetails = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  flex: 1;
`;

const FileRowName = styled.div`
  color: #323232;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const FileRowSubtitle = styled.div`
  color: #888;
  font-size: 12px;
  margin-top: 2px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const FilesEmpty = styled.div`
  color: #888;
  font-size: 14px;
  font-style: italic;
  padding: 12px 0;
`;

/* Persona generation status banner (shown while async LLM job is running) */
const PersonaStatusBanner = styled.div<{ $tone: 'info' | 'error' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin: 8px 0 0;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid ${(p) => (p.$tone === 'error' ? '#fca5a5' : '#bfdbfe')};
  background: ${(p) => (p.$tone === 'error' ? '#fef2f2' : '#eff6ff')};
  color: ${(p) => (p.$tone === 'error' ? '#991b1b' : '#1e40af')};
`;

const PersonaStatusSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid #bfdbfe;
  border-top-color: #1d4ed8;
  border-radius: 50%;
  flex-shrink: 0;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/* Overview card — matches .card + .header + .content */
const OverviewCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: transparent;
  border-radius: 8px;
  /* Horizontal margin matches the PageRenderer's RGL containerPadding (36px)
     so the Segment Overview card aligns with the Segment Characteristics /
     Segmentation Model Results blocks rendered below. */
  margin: 8px 36px 16px;
  border: 1px solid #e5e5e5;
  background-color: #ffffff;
`;

const OverviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #ffffff;
  border-radius: 8px 8px 0 0;
`;

const OverviewTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #323232;
`;

const Divider = styled.div`
  height: 1px;
  min-height: 1px;
  width: 100%;
  background-color: #e5e5e5;
`;

const OverviewContent = styled.div`
  background: transparent;
  padding: 14px 16px;
`;

/* Narrative + action boxes inside the overview content */
const NarrativeText = styled.p`
  font-size: 14px;
  line-height: 1.7;
  color: #626262;
  margin: 0 0 16px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const ActionBox = styled.div<{ $variant: 'action' | 'gap' }>`
  flex: 1;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: #4c4c4c;
  line-height: 1.6;
  ${(p) =>
    p.$variant === 'action'
      ? `
    background: #e3f9e8;
    border: 1px solid #4CB847;
  `
      : `
    background: #fffbeb;
    border: 1px solid #f59e0b;
  `}
`;

const ActionLabel = styled.div<{ $variant: 'action' | 'gap' }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  color: ${(p) => (p.$variant === 'action' ? '#2d7a2e' : '#92400e')};
`;

/* Page area for the auto-generated dashboard widgets */
const PageArea = styled.div<{ $contained?: boolean }>`
  padding: 0;
  position: relative;
  /* Offset the BuilderCanvas containerPadding so blocks align with sibling cards */
  ${(p) =>
    p.$contained
      ? `margin: 0; width: 100%;`
      : `margin-left: -36px; margin-right: -36px; width: calc(100% + 72px);`}
`;

const EditDashboardToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 36px 4px;
`;

const EditDashboardBtn = styled.button`
  border: 1px solid #1976d2;
  border-radius: 6px;
  background: transparent;
  color: #1976d2;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 16px;
  cursor: pointer;
  transition: all 120ms ease;
  &:hover {
    background: #e3f2fd;
  }
`;

/* ─── Edit-mode sub-tabs ──────────────────────────────────────────────────── */
const EditSubTabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e5e5;
  margin-bottom: 16px;
`;

const EditSubTab = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? '#1271c4' : 'transparent')};
  margin-bottom: -2px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) => (p.$active ? '#1271c4' : '#626262')};
  cursor: pointer;
  font-family: inherit;
  transition:
    color 0.12s,
    border-color 0.12s;
  &:hover {
    color: #1271c4;
  }
`;

/* ─── Section order manager ───────────────────────────────────────────────── */
const SectionOrderCard = styled.div`
  background: #f8f9fb;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const SectionOrderTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #626262;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const SectionOrderRow = styled.div<{ $dimmed?: boolean; $dragging?: boolean; $dragOver?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  border-radius: 4px;
  opacity: ${(p) => (p.$dimmed ? 0.5 : 1)};
  background: ${(p) => (p.$dragging ? '#e3edf9' : p.$dragOver ? '#f0f4ff' : 'transparent')};
  border: 1px dashed ${(p) => (p.$dragOver ? '#1271c4' : 'transparent')};
  transition:
    background 0.12s,
    border-color 0.12s;
  &:hover {
    background: ${(p) => (p.$dragging ? '#e3edf9' : '#eef2f7')};
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  color: #999;
  &:active {
    cursor: grabbing;
  }
`;

const SectionOrderLabel = styled.span`
  flex: 1;
  font-size: 14px;
  color: #323232;
`;

const VisibilityBtn = styled.button`
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: #626262;
  display: flex;
  align-items: center;
  border-radius: 3px;
  &:hover {
    background: #dde4ed;
    color: #323232;
  }
`;

export type SegmentProfileScreenProps = {
  /** When set, use this pageId instead of reading from route params.
   *  Lets the project subtab container resolve `:id` → dashboardPageId
   *  and render this screen without duplicating its body. */
  pageIdOverride?: number;
};

export function SegmentProfileScreen({ pageIdOverride }: SegmentProfileScreenProps = {}): React.JSX.Element {
  useForceHackathonBrand();

  const { pageId: pageIdParam } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const apiClient = useChatApiClient();
  const brandIdentifier = useActiveBrandIdentifier();

  const proxyFetch = useCallback(
    ({ agentId, extensionPath }: { agentId: number; extensionPath: string }) =>
      apiClient.pages.proxyWidgetData(agentId, extensionPath, brandIdentifier ?? undefined),
    [apiClient, brandIdentifier]
  );

  const numericPageId = pageIdOverride ?? (pageIdParam ? +pageIdParam : NaN);

  // Project name from the parent ProjectDetailsContext when rendered inside
  // a project subtab. Falls back to segmentProfile._projectTitle when the
  // screen is mounted from the global segmentation builder route (which
  // doesn't provide the context).
  const projectDetailsCtx = useContext(ProjectDetailsContext);
  const projectNameFromContext = projectDetailsCtx?.query?.data?.project?.name ?? null;
  const { data: pageData } = usePage(isNaN(numericPageId) ? null : numericPageId);
  // Probe-segment chat panel removed — the global ChatWidgetContainer
  // already provides a lightbulb on this route, and the purple duplicate
  // was confusing users. Chat comes from the global widget now.
  const pageAreaRef = useRef<HTMLDivElement>(null);

  // Segment DB record — used for marketView + metadata.quotes.
  //
  // Explore Segments passes a record through navigation state as a fast
  // path so the hero/narrative can paint in the first frame. That record
  // may be SKINNY (metadata._listView === true) because Explore uses
  // view=list for its bulk fetch. When it's skinny we still paint the
  // hero instantly, then upgrade to the fat row via
  // GET /segments/by-page/:pageId in the background so the market view
  // panel and quotes card can hydrate.
  //
  // When no nav state is present (cold URL load / hard refresh), we fetch
  // the fat row by-page directly.
  const initialRecord = state?.segmentDbRecord ?? null;
  const initialIsSkinny = isSkinnyRecord(initialRecord);
  const [segmentDbRecord, setSegmentDbRecord] = useState<Record<string, unknown> | null>(initialRecord);
  // Loading flag only blocks nothing full-page any more — components
  // conditionally render when their slice of metadata arrives. We track
  // it so SegmentMarketViewPanel/Insights can optionally show skeletons.
  const [segmentDbLoading, setSegmentDbLoading] = useState(
    (initialRecord == null || initialIsSkinny) && !isNaN(numericPageId)
  );
  useEffect(() => {
    // Skip network only when we already have the FAT row.
    if (segmentDbRecord && !isSkinnyRecord(segmentDbRecord)) return;
    if (isNaN(numericPageId)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSegmentDbLoading(false);
      return;
    }
    let cancelled = false;
    setSegmentDbLoading(true);
    getSegmentByPage(numericPageId)
      .then(({ segment }) => {
        if (cancelled || !segment) return;
        setSegmentDbRecord(segment);
      })
      .catch(() => {
        // Fall back to the legacy full-list scan only if the targeted
        // lookup fails entirely (404 / transient error). This preserves
        // the old behavior for cold-load edge cases.
        if (cancelled) return;
        listSegments({ hackathon: true })
          .then(({ segments }) => {
            if (cancelled) return;
            const match = segments.find(
              (s) => (s as Record<string, unknown>).dashboardPageId === numericPageId
            );
            if (match) setSegmentDbRecord(match as Record<string, unknown>);
          })
          .catch(() => {});
      })
      .finally(() => {
        if (!cancelled) setSegmentDbLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // segmentDbRecord is intentionally read only on mount / page change —
    // the guard above prevents refetches once the fat row is in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericPageId]);

  const marketViewData = useMemo(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    return (meta?.marketView as Record<string, unknown> | undefined) ?? null;
  }, [segmentDbRecord]);

  // Tab state for Overview / Synthetic Audience / Files / Message Generator.
  const [activeTab, setActiveTab] = useState<'overview' | 'synthetic' | 'files' | 'message_generator'>(
    'overview'
  );

  // Chat sidebar open/closed state - default closed
  const [chatOpen, setChatOpen] = useState(false);

  // segmentation_run_id is the new v2 FK path written by useSaveSegmentPages.
  // When present, the Files tab reads from `run.files` (durable, snapshotted
  // at save time). When absent — old segments, or failed run writes — the
  // tab falls back to listing all files under project 11.
  const segmentationRunId = useMemo<number | undefined>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    const direct = meta?.segmentationRunId as number | undefined;
    if (typeof direct === 'number') return direct;
    // Legacy segments have no run FK. Return undefined so we fall back.
    return undefined;
  }, [segmentDbRecord]);

  const [runForFiles, setRunForFiles] = useState<SegmentationRunRecord | null>(null);
  const [projectFiles, setProjectFiles] = useState<ProjectFileItem[] | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [filesSource, setFilesSource] = useState<'run' | 'project' | null>(null);

  // Hardcoded fallback project id (matches useSaveSegmentPages)
  const FALLBACK_PROJECT_ID = 11;

  useEffect(() => {
    if (activeTab !== 'files') return;
    if (runForFiles !== null || projectFiles !== null) return; // already loaded

    let cancelled = false;
    setFilesLoading(true);
    setFilesError(null);

    const loadFromRun = async (): Promise<boolean> => {
      if (segmentationRunId == null) return false;
      try {
        const { run } = await getSegmentationRun(segmentationRunId);
        if (cancelled) return true;
        setRunForFiles(run);
        setFilesSource('run');
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[segment-profile] failed to load run for files, falling back to project', err);
        return false;
      }
    };

    const loadFromProject = async (): Promise<void> => {
      try {
        const resp = await getProjectFiles(FALLBACK_PROJECT_ID);
        if (cancelled) return;
        setProjectFiles(resp.items ?? []);
        setFilesSource('project');
      } catch (err) {
        if (cancelled) return;
        setFilesError(err instanceof Error ? err.message : 'Failed to load project files');
        setProjectFiles([]);
      }
    };

    void (async () => {
      const loaded = await loadFromRun();
      if (!loaded && !cancelled) {
        await loadFromProject();
      }
      if (!cancelled) setFilesLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, segmentationRunId, runForFiles, projectFiles]);

  const segmentQuotesData = useMemo<SegmentQuotesData | null>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    const quotes = meta?.quotes as SegmentQuotesData | undefined;
    return quotes ?? null;
  }, [segmentDbRecord]);

  // Derive the async quote-extraction status from segment metadata.
  // 'processing' = queue job is running; 'complete' = quotes available;
  // 'failed' = extraction failed; undefined = not applicable (no qual).
  const quoteStatus = useMemo(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    return meta?._quoteStatus as string | undefined;
  }, [segmentDbRecord]);

  // Poll for quote completion when the segment was saved with async
  // quote extraction. Checks every 5s until status is 'complete' or 'failed'.
  useEffect(() => {
    if (quoteStatus !== 'processing') return;
    const segId = segmentDbRecord?.id as number | undefined;
    if (!segId) return;

    let cancelled = false;
    const poll = () => {
      if (cancelled) return;
      getSegmentQuoteStatus(segId)
        .then(({ quoteStatus: status, quotes }) => {
          if (cancelled) return;
          if (status === 'complete' || status === 'failed') {
            // Patch the local record so quotes render without a full refetch
            setSegmentDbRecord((prev) => {
              if (!prev) return prev;
              const meta: Record<string, unknown> = {
                ...(prev.metadata as Record<string, unknown>),
                _quoteStatus: status,
              };
              if (quotes) meta.quotes = quotes;
              return { ...prev, metadata: meta };
            });
          }
        })
        .catch(() => {
          /* retry on next tick */
        });
    };

    // Initial check after a short delay, then every 5s
    const initialTimer = setTimeout(poll, 2000);
    const interval = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [quoteStatus, segmentDbRecord?.id]);

  // ─── Persona generation polling ──────────────────────────────────────────
  // The persona-generation worker writes `segments_v2.persona` + flips
  // `_personaStatus` on segments_v2.metadata. SegmentProfile reads the
  // legacy `segments` row today, so we poll the v2 sibling directly via
  // GET /segments/v2/:v2Id/persona-status until it resolves. The v2 id is
  // stamped into the legacy metadata at create time by POST /segments.
  const segmentV2IdFromMeta = useMemo<number | undefined>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    const v = meta?.segmentV2Id as number | undefined;
    return typeof v === 'number' ? v : undefined;
  }, [segmentDbRecord]);

  const [personaStatus, setPersonaStatus] = useState<'pending' | 'generating' | 'complete' | 'failed' | null>(
    null
  );
  const [personaMarkdown, setPersonaMarkdown] = useState<string | null>(null);

  useEffect(() => {
    if (segmentV2IdFromMeta == null) {
      setPersonaStatus(null);
      setPersonaMarkdown(null);
      return;
    }

    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const poll = () => {
      if (cancelled) return;
      getSegmentPersonaStatus(segmentV2IdFromMeta)
        .then(({ personaStatus: status, persona }) => {
          if (cancelled) return;
          setPersonaStatus(status);
          setPersonaMarkdown(persona);
          if (status === 'complete' || status === 'failed' || status === 'pending') {
            if (interval) {
              clearInterval(interval);
              interval = null;
            }
          }
        })
        .catch(() => {
          /* retry on next tick */
        });
    };

    // Initial check, then every 5s while generating
    const initialTimer = setTimeout(poll, 1000);
    interval = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      if (interval) clearInterval(interval);
    };
  }, [segmentV2IdFromMeta]);

  // Blur widget cards whose title matches a known synthetic/preview title so
  // sample charts are visually distinguished from real ones. The list of
  // preview titles lives alongside buildSegmentDashboard so the two stay in
  // sync. Runs after PageRenderer mounts and on every subsequent DOM change
  // inside PageArea.
  // Blur preview widgets in the Segment Characteristics block (old segments only —
  // new saves skip this block entirely via skipCharacteristicsBlock).
  useEffect(() => {
    const container = pageAreaRef.current;
    if (!container) return;
    const applyBlur = (): void => {
      const allTitles = container.querySelectorAll<HTMLElement>('.MuiTypography-root');
      let blockGridItem: HTMLElement | null = null;
      for (const t of Array.from(allTitles)) {
        if ((t.textContent || '').trim() === 'Segment Characteristics') {
          blockGridItem = t.closest<HTMLElement>('.react-grid-item');
          break;
        }
      }
      if (!blockGridItem) return;

      // Blur preview widgets (old segments without market view data).
      const widgetTitles = blockGridItem.querySelectorAll<HTMLElement>('.MuiTypography-root');
      widgetTitles.forEach((titleEl) => {
        if ((titleEl.textContent || '').trim() === 'Segment Characteristics') return;
        const widgetContainer = titleEl.parentElement;
        if (!widgetContainer || widgetContainer.dataset.charBlurred === 'true') return;
        widgetContainer.dataset.charBlurred = 'true';
        widgetContainer.style.position = 'relative';
        Array.from(widgetContainer.children).forEach((child) => {
          if (child === titleEl) return;
          const el = child as HTMLElement;
          el.style.filter = 'blur(6px)';
          el.style.pointerEvents = 'none';
          el.style.userSelect = 'none';
        });
        const overlay = document.createElement('div');
        overlay.style.cssText =
          'position:absolute;inset:0;top:32px;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:1;';
        overlay.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;gap:6px;background:#ffffff;padding:16px 24px;border-radius:8px;border:1px solid #e5e5e5;box-shadow:0 2px 8px rgba(0,0,0,0.06);">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#626262" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span style="font-size:12px;color:#626262;font-weight:500;">Insights Pending</span>
          </div>
        `;
        widgetContainer.appendChild(overlay);
      });
    };
    const t = setTimeout(applyBlur, 400);
    const observer = new MutationObserver(() => applyBlur());
    observer.observe(container, { childList: true, subtree: true });
    return () => {
      clearTimeout(t);
      observer.disconnect();
    };
  }, [numericPageId, pageData, marketViewData]);

  // Force-collapse the "Segment Model Results" block on mount
  useEffect(() => {
    const container = pageAreaRef.current;
    if (!container) return;
    const collapseModelResults = (): void => {
      const allTitles = container.querySelectorAll<HTMLElement>('.MuiTypography-root');
      for (const t of Array.from(allTitles)) {
        if (
          (t.textContent || '').trim() === 'Segment Model Results' ||
          (t.textContent || '').trim() === 'Segmentation Model Results'
        ) {
          // Find the MUI Accordion — it has aria-expanded
          const accordion = t.closest<HTMLElement>('.MuiAccordion-root');
          if (accordion) {
            const summary = accordion.querySelector<HTMLElement>('.MuiAccordionSummary-root');
            if (summary?.getAttribute('aria-expanded') === 'true') {
              summary.click();
            }
          }
          break;
        }
      }
    };
    const t = setTimeout(collapseModelResults, 500);
    return () => clearTimeout(t);
  }, [numericPageId, pageData]);

  // ─── Overview inline editing ────────────────────────────────────────────
  const handleOverviewSave = useCallback(
    async (fields: {
      narrative?: string;
      commercialAction?: string;
      gapAnalysis?: string | Record<string, unknown>;
    }) => {
      if (segmentV2IdFromMeta == null) return;
      const { segment } = await patchSegmentMetadata(segmentV2IdFromMeta, {
        segmentProfile: {
          profile_narrative: fields.narrative,
          commercial_action: fields.commercialAction,
          gap_analysis: fields.gapAnalysis,
        },
      });
      if (segment) setSegmentDbRecord(segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta]
  );

  // ─── Quotes inline editing (hide/restore) ──────────────────────────────
  const handleQuotesSave = useCallback(
    async (updatedQuotes: SegmentQuotesData) => {
      if (segmentV2IdFromMeta == null) return;
      const { segment } = await patchSegmentMetadata(segmentV2IdFromMeta, {
        quotes: updatedQuotes,
      });
      if (segment) setSegmentDbRecord(segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta]
  );

  // ─── Characteristics section visibility ─────────────────────────────────
  const hiddenCharacteristicsSections = useMemo<CharacteristicsSection[]>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    return (meta?.hiddenCharacteristicsSections as CharacteristicsSection[] | undefined) ?? [];
  }, [segmentDbRecord]);

  const handleCharacteristicsSave = useCallback(
    async (sections: CharacteristicsSection[]) => {
      if (segmentV2IdFromMeta == null) return;
      const { segment } = await patchSegmentMetadata(segmentV2IdFromMeta, {
        hiddenCharacteristicsSections: sections,
      });
      if (segment) setSegmentDbRecord(segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta]
  );

  // ─── Global section edit mode ────────────────────────────────────────────
  // When true, per-section pencil icons appear. Toggled by a single pencil
  // button next to the Manage button in the breadcrumbs.
  const [sectionEditMode, setSectionEditMode] = useState(false);

  // Sub-tab inside edit mode: "sections" vs "dashboard"
  const [editSubTab, setEditSubTab] = useState<'sections' | 'dashboard'>('sections');

  // ─── Section ordering ───────────────────────────────────────────────────
  const sectionOrder = useMemo<SectionOrderItem[]>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    const stored = meta?.sectionOrder as SectionOrderItem[] | undefined;
    if (stored && Array.isArray(stored) && stored.length > 0) return stored;
    return DEFAULT_SECTION_ORDER;
  }, [segmentDbRecord]);

  const handleSectionOrderSave = useCallback(
    async (order: SectionOrderItem[]) => {
      if (segmentV2IdFromMeta == null) return;
      const { segment } = await patchSegmentMetadata(segmentV2IdFromMeta, {
        sectionOrder: order,
      });
      if (segment) setSegmentDbRecord(segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta]
  );

  // Drag-and-drop reordering state
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDragStart = useCallback((idx: number) => {
    setDragIdx(idx);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  }, []);

  const handleDrop = useCallback(
    (targetIdx: number) => {
      if (dragIdx == null || dragIdx === targetIdx) {
        setDragIdx(null);
        setDragOverIdx(null);
        return;
      }
      const next = [...sectionOrder];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(targetIdx, 0, moved);
      handleSectionOrderSave(next);
      setDragIdx(null);
      setDragOverIdx(null);
    },
    [dragIdx, sectionOrder, handleSectionOrderSave]
  );

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setDragOverIdx(null);
  }, []);

  const toggleSectionHidden = useCallback(
    (idx: number) => {
      const next = [...sectionOrder];
      next[idx] = { ...next[idx], hidden: !next[idx].hidden };
      handleSectionOrderSave(next);
    },
    [sectionOrder, handleSectionOrderSave]
  );

  // ─── Page builder edit mode ─────────────────────────────────────────────
  const [pageBuilderEditing, setPageBuilderEditing] = useState(false);

  const pageBuilderApi = useMemo((): SegmentPageBuilderApi | null => {
    if (segmentV2IdFromMeta == null) return null;
    const segId = segmentV2IdFromMeta;
    return {
      getSegmentPage: async () => {
        const res = await apiClient.authenticatedClient.get(`/segments/${segId}/page`);
        const page = res.data;
        return {
          id: page.id,
          name: page.name,
          config: page.currentVersion?.config ?? page.config ?? {},
        };
      },
      savePageVersion: async (_pId, _sId, data) => {
        const res = await apiClient.authenticatedClient.post(`/segments/${segId}/page/version`, data);
        return res.data;
      },
      revertPageVersion: async (_pId, _sId, versionId) => {
        const res = await apiClient.authenticatedClient.post(`/segments/${segId}/page/revert/${versionId}`);
        return res.data;
      },
    };
  }, [segmentV2IdFromMeta, apiClient]);

  // Segment data sources, in precedence order (lowest → highest):
  //   1. pageData.providerData.segmentProfile  — legacy PageBuilder storage
  //   2. segmentDbRecord.metadata.segmentProfile — FULL shape from the
  //        /segments/by-page upgrade (includes commercial_action,
  //        gap_analysis, archetype_distribution, key_stats, etc.)
  //   3. state.segment                         — whatever Explore passed
  //        through navigation state (may be a SKINNY list-view shape)
  //
  // We merge them together so the hero paints instantly from (3) and fills
  // in with the fat fields from (2) as soon as the by-page upgrade
  // resolves. Source (2) is the authoritative "full row" — without it,
  // commercial_action + gap_analysis go missing whenever Explore navigates
  // through the skinny view=list flow.
  const seg = useMemo(() => {
    const legacyStored = (pageData?.providerData as Record<string, unknown> | undefined)?.segmentProfile as
      | Record<string, unknown>
      | undefined;
    const dbStored = (segmentDbRecord?.metadata as Record<string, unknown> | undefined)?.segmentProfile as
      | Record<string, unknown>
      | undefined;
    const merged = {
      ...(legacyStored ?? {}),
      ...(dbStored ?? {}),
      ...(state?.segment ?? {}),
    };
    return Object.keys(merged).length > 0 ? (merged as unknown as SegmentData) : null;
  }, [state?.segment, pageData, segmentDbRecord]);

  // ─── Wire the global chat widget to this segment's context ──────────────
  // While this screen is mounted, we override the global widget's default
  // chat context with a hackathon-agent context scoped to THIS segment
  // (segmentIdentifier + segmentProfile metadata). When the chat opens it
  // will use the segment's first-person persona via segments_v2.persona
  // (see provider.genai-platform.ts getSegment + buildSegmentPersona on
  // the backend). On unmount, clear the override so subsequent pages
  // fall back to the default (hackathon-agent bare for /projects/11/*).
  const dispatchChatContext = useContext(DispatchPageContextDataContext);
  const sessionId = useSessionStore((s) => s.sessionId);

  // Load the chat contexts list once so we can resolve the hackathon
  // agent. We deliberately use hackathon-agent instead of the frontend's
  // `bi-project` identifier because `bi-project` triggers the chat-api's
  // buildAgentMetadata branch that calls the brand-insights backend —
  // which is not configured on this stack (getBrandInsightsClient throws
  // "BRANDINSIGHTS_API_URL is not configured"). `hackathon-agent` takes
  // the safe no-op branch in buildAgentMetadata.
  const { data: chatAgents } = useQuery<ChatContextItem[]>({
    queryKey: ['chat-contexts', brandIdentifier],
    queryFn: () =>
      apiClient.chat.getChatContexts({ brandIdentifier: brandIdentifier! }).then((res) => res.items),
    enabled: !!brandIdentifier,
    staleTime: 5 * 60 * 1000,
  });

  // Build the segment-scoped chat context. Uses the hackathon agent as the
  // base (proven working via ChatInterfaceNative) and attaches the segment
  // as an immutable tag filter. The workspace.qa-agent on the backend reads
  // `tags[].metadata.segmentIdentifier` directly from the chat session's
  // filter state — not via the brand-insights hints endpoint — so the
  // segment persona still lights up through workspace.qa-agent → getSegment
  // → buildSegmentPersona without needing brand-insights configured.
  //
  // NOTE: `initialMessage` + `questions` overrides do NOT work on this
  // stack today — the hints endpoint at chat-api/src/routers/v1/chat/hints.ts
  // gates that segment lookup behind `isBrandInsightsContext` which is
  // false for hackathon-agent. We'd need to lift that check to make them
  // flow through. Deferred.
  const syntheticAudienceChatContext = useMemo<BrandInsightsPageContext.Agent | null>(() => {
    if (!chatAgents?.length || !seg) return null;
    const hackathonAgent = chatAgents.find((a) => a.agent?.identifier?.startsWith('hackathon-agent'));
    if (!hackathonAgent) return null;

    const identifier = segmentDbRecord?.identifier as string | undefined;
    return {
      type: ChatContextType.Agent,
      data: hackathonAgent,
      immutableFilters: identifier
        ? {
            tags: [
              {
                id: 0,
                name: `Segment: ${seg.name}`,
                metadata: {
                  canRemove: false,
                  segmentIdentifier: identifier,
                },
              },
            ],
          }
        : undefined,
      metadata: {
        ...(sessionId ? { sessionId } : {}),
        ...(identifier ? { segmentIdentifier: identifier } : {}),
        segmentProfile: {
          name: seg.name,
          size: seg.size,
          pct: seg.pct,
          profile_narrative: seg.profile_narrative,
          commercial_action: seg.commercial_action,
          archetype_distribution: seg.archetype_distribution,
          momentum_distribution: seg.momentum_distribution,
          distinguishing_features: seg.distinguishing_features,
          top_molecules: seg.top_molecules,
        },
      },
    } as unknown as BrandInsightsPageContext.Agent;
  }, [chatAgents, seg, segmentDbRecord, sessionId]);

  useEffect(() => {
    if (!dispatchChatContext || !syntheticAudienceChatContext) return;
    dispatchChatContext(syntheticAudienceChatContext);

    return () => {
      // Reset on unmount so subsequent non-segment pages on /projects/11/*
      // pick up the bare hackathon-agent default from HackathonChatContextBridge.
      dispatchChatContext(null as unknown as BrandInsightsPageContext.Item);
    };
  }, [dispatchChatContext, syntheticAudienceChatContext]);

  const segIndex = state?.segmentIndex ?? 0;
  // Prefer the DB row's iconUrl (set via SegmentManage) so the header
  // matches what the chat shows. Falls back to the deterministic preset
  // when no icon has been chosen yet.
  const dbIconUrl = (segmentDbRecord?.iconUrl as string | null | undefined) ?? null;
  const avatarUrl = dbIconUrl || getSegmentAvatar(segIndex);

  // NOTE: intentionally no full-page spinner while segmentDbLoading is true.
  // The hero header, narrative, tags, and dashboard all render from data
  // that is already available (location.state + usePage), so blocking the
  // whole screen on the DB record fetch makes the UX feel much slower than
  // it needs to be. Sections that specifically depend on the DB record —
  // SegmentMarketViewPanel, SegmentMarketResearchInsights — render
  // conditionally further down once marketViewData / segmentQuotesData
  // become available.

  if (isNaN(numericPageId)) {
    return (
      <Root>
        <LeftModule $narrow={true}>
          <Wrap>
            <Breadcrumbs>
              <BackLink onClick={() => navigate(-1)}>
                <ArrowLeft size={14} /> Back to Segmentation
              </BackLink>
            </Breadcrumbs>
            <div style={{ padding: 40, textAlign: 'center', color: '#ADADAD' }}>
              Invalid page.{' '}
              <button
                onClick={() => navigate(-1)}
                style={{
                  color: '#1271c4',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Return to segmentation
              </button>
            </div>
          </Wrap>
        </LeftModule>
      </Root>
    );
  }

  // Build tags from top archetype/momentum
  const tags: string[] = [];
  if (seg?.archetype_distribution) {
    const top = Object.entries(seg.archetype_distribution).sort((a, b) => b[1] - a[1])[0];
    if (top) tags.push(top[0]);
  }
  if (seg?.momentum_distribution) {
    const top = Object.entries(seg.momentum_distribution).sort((a, b) => b[1] - a[1])[0];
    if (top) tags.push(top[0]);
  }

  const totalHcps = seg?.size ? Math.round(seg.size / (seg.pct || 1)) : undefined;

  // Resolve the project id when rendered inside a project subtab so the
  // Manage button can navigate to /projects/:projectId/segments/:v2Id/manage.
  const parentProjectId = projectDetailsCtx?.query?.data?.project?.id;
  const manageTo =
    segmentV2IdFromMeta != null
      ? parentProjectId != null
        ? `/projects/${parentProjectId}/segments/${segmentV2IdFromMeta}/manage`
        : `/segments/${segmentV2IdFromMeta}/manage`
      : null;

  return (
    <Root>
      <LeftModule $chatOpen={chatOpen && activeTab !== 'message_generator'}>
        <Wrap>
          {/* Breadcrumbs + Manage button */}
          <Breadcrumbs>
            <BackLink onClick={() => navigate(-1)}>
              <ArrowLeft size={14} /> Back to Segmentation
            </BackLink>
            <BreadcrumbActions>
              {segmentV2IdFromMeta != null && (
                <EditModeBtn
                  onClick={() => {
                    setSectionEditMode((v) => {
                      if (v) {
                        // Switching to View – reset all editing sub-states
                        setPageBuilderEditing(false);
                        setEditSubTab('sections');
                      }
                      return !v;
                    });
                  }}
                  $active={sectionEditMode}
                >
                  {sectionEditMode ? 'View' : 'Edit'}
                </EditModeBtn>
              )}
              {manageTo && (
                <ManageBtn onClick={() => navigate(manageTo)} title="Manage segment">
                  Manage
                </ManageBtn>
              )}
            </BreadcrumbActions>
          </Breadcrumbs>

          {/* Profile header card + tab bar — sticky so they remain visible while scrolling */}
          <StickyHeaderArea>
            <ProfileCard>
              {/* SegmentAvatar includes the "Chat with Segment" ring that
                jumps to the Synthetic Audience tab — ported from the old
                /segments page. */}
              <SegmentAvatar
                iconUrl={avatarUrl}
                name={seg?.name || 'Segment'}
                onChatWithSegment={() => setActiveTab('synthetic')}
                size={90}
              />

              <LeftSection>
                <ProfileName>{seg?.name || `Segment ${segIndex + 1}`}</ProfileName>
                <ProfileSubtitle>
                  {seg?.size?.toLocaleString() ?? '—'} HCPs ({((seg?.pct || 0) * 100).toFixed(0)}%)
                </ProfileSubtitle>
              </LeftSection>

              <RightInfoColumn>
                <MetaBoxesRow>
                  <MetaBox>
                    <MetaBoxLabel>Version</MetaBoxLabel>
                    <MetaBoxValue>v1.0</MetaBoxValue>
                  </MetaBox>
                  <MetaBox>
                    <MetaBoxLabel>Sample Size</MetaBoxLabel>
                    <MetaBoxValue>
                      {seg?.size?.toLocaleString() ?? '—'}
                      {totalHcps ? ` / ${totalHcps.toLocaleString()}` : ''}
                    </MetaBoxValue>
                  </MetaBox>
                  <MetaBox>
                    <MetaBoxLabel>Project</MetaBoxLabel>
                    <MetaBoxValue>
                      {projectNameFromContext ||
                        ((seg as unknown as Record<string, unknown> | null)?._projectTitle as string) ||
                        'Untitled'}
                    </MetaBoxValue>
                  </MetaBox>
                </MetaBoxesRow>
                {tags.length > 0 && (
                  <Tags>
                    {tags.map((t) => (
                      <Tag key={t}>{t}</Tag>
                    ))}
                  </Tags>
                )}
              </RightInfoColumn>
            </ProfileCard>

            {/* Tab bar */}
            <TabBarWrap>
              <Tab $active={activeTab === 'overview'} $clickable onClick={() => setActiveTab('overview')}>
                Overview
              </Tab>
              <Tab
                $active={activeTab === 'message_generator'}
                $clickable
                onClick={() => setActiveTab('message_generator')}
              >
                Message Generator
              </Tab>
              <Tab $active={activeTab === 'files'} $clickable onClick={() => setActiveTab('files')}>
                Files
              </Tab>
              <Tab $active={activeTab === 'synthetic'} $clickable onClick={() => setActiveTab('synthetic')}>
                Synthetic Audience
              </Tab>
              <TabBarLine />
              <TabIndicator
                $left={
                  activeTab === 'overview'
                    ? 0
                    : activeTab === 'message_generator'
                      ? 150
                      : activeTab === 'files'
                        ? 300
                        : 450
                }
              />
            </TabBarWrap>
          </StickyHeaderArea>

          {/* Persona generation status — shown only while the async LLM job
             is running or just after it completes / fails. Hidden on
             Synthetic Audience tab and once the segment has a stable persona. */}
          {activeTab !== 'synthetic' && personaStatus === 'generating' && (
            <PersonaStatusBanner $tone="info">
              <PersonaStatusSpinner />
              <span>
                Generating chat persona for this segment… The digital-twin chat will use this voice once it's
                ready.
              </span>
            </PersonaStatusBanner>
          )}
          {activeTab !== 'synthetic' && personaStatus === 'failed' && (
            <PersonaStatusBanner $tone="error">
              <span>Persona generation failed. Chat will fall back to a generic voice until you retry.</span>
            </PersonaStatusBanner>
          )}

          {activeTab === 'overview' && (
            <>
              {/* ──── Edit mode: sub-tabs ──────────────────────────────── */}
              {sectionEditMode ? (
                <>
                  <EditSubTabBar>
                    <EditSubTab
                      $active={editSubTab === 'sections'}
                      onClick={() => {
                        setEditSubTab('sections');
                        setPageBuilderEditing(false);
                      }}
                    >
                      Sections
                    </EditSubTab>
                    <EditSubTab
                      $active={editSubTab === 'dashboard'}
                      onClick={() => {
                        setEditSubTab('dashboard');
                        setPageBuilderEditing(true);
                      }}
                    >
                      Custom Dashboard
                    </EditSubTab>
                  </EditSubTabBar>

                  {editSubTab === 'sections' && (
                    <>
                      {/* Section order manager */}
                      <SectionOrderCard>
                        <SectionOrderTitle>Section Order</SectionOrderTitle>
                        {sectionOrder.map((item, idx) => (
                          <SectionOrderRow
                            key={item.key}
                            $dimmed={item.hidden}
                            $dragging={dragIdx === idx}
                            $dragOver={dragOverIdx === idx}
                            draggable
                            onDragStart={() => handleDragStart(idx)}
                            onDragOver={(e) => handleDragOver(e, idx)}
                            onDrop={() => handleDrop(idx)}
                            onDragEnd={handleDragEnd}
                          >
                            <DragHandle>
                              <DragIndicatorIcon fontSize="small" />
                            </DragHandle>
                            <SectionOrderLabel>{SECTION_LABELS[item.key]}</SectionOrderLabel>
                            <VisibilityBtn
                              onClick={() => toggleSectionHidden(idx)}
                              title={item.hidden ? 'Show section' : 'Hide section'}
                            >
                              {item.hidden ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </VisibilityBtn>
                          </SectionOrderRow>
                        ))}
                      </SectionOrderCard>

                      {/* Render auto-generated sections in order */}
                      {sectionOrder
                        .filter((s) => !s.hidden && s.key !== 'custom_dashboard')
                        .map((s) => {
                          switch (s.key) {
                            case 'overview':
                              return marketViewData ? (
                                <div key={s.key}>
                                  <SegmentOverviewCard
                                    narrative={seg?.profile_narrative}
                                    commercialAction={seg?.commercial_action}
                                    gapAnalysis={seg?.gap_analysis}
                                    editMode
                                    onSave={segmentV2IdFromMeta != null ? handleOverviewSave : undefined}
                                  />
                                  <div style={{ height: 16 }} />
                                </div>
                              ) : null;
                            case 'characteristics':
                              return marketViewData ? (
                                <div key={s.key}>
                                  <SegmentMarketViewPanel
                                    data={marketViewData}
                                    segmentName={seg?.name}
                                    hiddenSections={hiddenCharacteristicsSections}
                                    editMode
                                    onSave={
                                      segmentV2IdFromMeta != null ? handleCharacteristicsSave : undefined
                                    }
                                  />
                                </div>
                              ) : null;
                            case 'market_research_insights':
                              return segmentQuotesData ? (
                                <SegmentMarketResearchInsights
                                  key={s.key}
                                  quotes={segmentQuotesData}
                                  segmentName={seg?.name}
                                  editMode
                                  onSave={segmentV2IdFromMeta != null ? handleQuotesSave : undefined}
                                />
                              ) : null;
                            default:
                              return null;
                          }
                        })}
                      {!segmentQuotesData && quoteStatus === 'processing' && (
                        <QuoteProcessingCard>
                          <QuoteProcessingSpinner />
                          <span>
                            Extracting HCP quotes for this segment… This typically takes around 1 minute to
                            complete.
                          </span>
                        </QuoteProcessingCard>
                      )}
                    </>
                  )}

                  {editSubTab === 'dashboard' && (
                    <PageArea ref={pageAreaRef} $contained>
                      {pageBuilderApi && segmentV2IdFromMeta != null ? (
                        <SegmentPageBuilderApiProvider value={pageBuilderApi}>
                          <SegmentPageBuilderContainer
                            projectId={
                              (segmentDbRecord?.projectId as number | undefined) ?? FALLBACK_PROJECT_ID
                            }
                            segmentId={segmentV2IdFromMeta}
                            title="Custom Dashboard"
                          />
                        </SegmentPageBuilderApiProvider>
                      ) : (
                        <PageRenderer
                          pageId={numericPageId}
                          proxyFetch={proxyFetch}
                          brandIdentifier={brandIdentifier ?? undefined}
                        />
                      )}
                    </PageArea>
                  )}
                </>
              ) : (
                /* ──── View mode: render sections in stored order ─────── */
                <>
                  {sectionOrder
                    .filter((s) => !s.hidden)
                    .map((s) => {
                      switch (s.key) {
                        case 'overview':
                          return marketViewData ? (
                            <div key={s.key}>
                              <SegmentOverviewCard
                                narrative={seg?.profile_narrative}
                                commercialAction={seg?.commercial_action}
                                gapAnalysis={seg?.gap_analysis}
                                editMode={false}
                              />
                              <div style={{ height: 16 }} />
                            </div>
                          ) : null;
                        case 'characteristics':
                          return marketViewData ? (
                            <div key={s.key}>
                              <SegmentMarketViewPanel
                                data={marketViewData}
                                segmentName={seg?.name}
                                hiddenSections={hiddenCharacteristicsSections}
                                editMode={false}
                              />
                            </div>
                          ) : null;
                        case 'market_research_insights':
                          return segmentQuotesData ? (
                            <SegmentMarketResearchInsights
                              key={s.key}
                              quotes={segmentQuotesData}
                              segmentName={seg?.name}
                              editMode={false}
                            />
                          ) : !segmentQuotesData && quoteStatus === 'processing' ? (
                            <QuoteProcessingCard key={s.key}>
                              <QuoteProcessingSpinner />
                              <span>
                                Extracting HCP quotes for this segment… This typically takes around 1 minute
                                to complete.
                              </span>
                            </QuoteProcessingCard>
                          ) : null;
                        case 'custom_dashboard':
                          return (
                            <PageArea key={s.key} ref={pageAreaRef}>
                              <PageRenderer
                                pageId={numericPageId}
                                proxyFetch={proxyFetch}
                                brandIdentifier={brandIdentifier ?? undefined}
                              />
                            </PageArea>
                          );
                        default:
                          return null;
                      }
                    })}
                </>
              )}
            </>
          )}
          {activeTab === 'synthetic' && (
            <SyntheticAudiencePanel>
              {(() => {
                const ttsSettings =
                  (segmentDbRecord?.ttsSettings as
                    | { avatarId?: string | null; ttsConfig?: unknown }
                    | null
                    | undefined) ?? null;
                return (
                  <DigitalTwinChat
                    avatarId={ttsSettings?.avatarId ?? undefined}
                    // Pass the segment-scoped chat context so the backend
                    // hints endpoint resolves the segment, returns its
                    // initialMessage as the welcome, maps its questions[]
                    // to starter hints, and the chat agent embodies the
                    // segment's persona via buildSegmentPersona.
                    chatPageContext={syntheticAudienceChatContext as never}
                    ttsConfig={(ttsSettings?.ttsConfig ?? null) as never}
                    disableVideoMode
                    segmentIconUrl={avatarUrl}
                  />
                );
              })()}
            </SyntheticAudiencePanel>
          )}
          {activeTab === 'files' && (
            <FilesPanel>
              {filesLoading && <FilesEmpty>Loading files…</FilesEmpty>}
              {!filesLoading && filesError && <FilesEmpty>Could not load files: {filesError}</FilesEmpty>}

              {/* ── Primary: run.files snapshot ────────────────────────────── */}
              {!filesLoading && !filesError && filesSource === 'run' && runForFiles && (
                <>
                  <FilesSection>
                    <FilesSectionTitle>
                      Source Data
                      {runForFiles.files.filter((f) => f.kind === 'quant').length > 0
                        ? ` (${runForFiles.files.filter((f) => f.kind === 'quant').length})`
                        : ''}
                    </FilesSectionTitle>
                    {(() => {
                      const quant = runForFiles.files.filter((f) => f.kind === 'quant');
                      if (quant.length === 0) {
                        return <FilesEmpty>No source dataset recorded for this run.</FilesEmpty>;
                      }
                      return (
                        <FilesList>
                          {quant.map((f) => (
                            <FileRow
                              key={f.id}
                              type="button"
                              title={f.filename}
                              onClick={() => {
                                /* no viewer wired yet — row is visually clickable */
                              }}
                            >
                              <FileRowIcon>
                                <FileIcon
                                  extension={f.extension || extensionOf(f.filename) || 'xlsx'}
                                  size={28}
                                />
                              </FileRowIcon>
                              <FileRowDetails>
                                <FileRowName>{f.filename}</FileRowName>
                                <FileRowSubtitle>Quant input</FileRowSubtitle>
                              </FileRowDetails>
                            </FileRow>
                          ))}
                        </FilesList>
                      );
                    })()}
                  </FilesSection>

                  <FilesSection>
                    <FilesSectionTitle>
                      Qualitative Transcripts
                      {runForFiles.files.filter((f) => f.kind === 'qual').length > 0
                        ? ` (${runForFiles.files.filter((f) => f.kind === 'qual').length})`
                        : ''}
                    </FilesSectionTitle>
                    {(() => {
                      const qual = runForFiles.files.filter((f) => f.kind === 'qual');
                      if (qual.length === 0) {
                        return (
                          <FilesEmpty>No qualitative transcripts were selected for this run.</FilesEmpty>
                        );
                      }
                      return (
                        <FilesList>
                          {qual.map((f) => (
                            <FileRow
                              key={f.id}
                              type="button"
                              title={f.filename}
                              onClick={() => {
                                /* no viewer wired yet — row is visually clickable */
                              }}
                            >
                              <FileRowIcon>
                                <FileIcon
                                  extension={f.extension || extensionOf(f.filename) || 'txt'}
                                  size={28}
                                />
                              </FileRowIcon>
                              <FileRowDetails>
                                <FileRowName>{basenameOf(f.filename)}</FileRowName>
                                <FileRowSubtitle>Transcript</FileRowSubtitle>
                              </FileRowDetails>
                            </FileRow>
                          ))}
                        </FilesList>
                      );
                    })()}
                  </FilesSection>
                </>
              )}

              {/* ── Fallback: project files library ─────────────────────────── */}
              {!filesLoading && !filesError && filesSource === 'project' && (
                <FilesSection>
                  <FilesSectionTitle>
                    Project Library
                    {projectFiles && projectFiles.length > 0 ? ` (${projectFiles.length})` : ''}
                  </FilesSectionTitle>
                  {!projectFiles || projectFiles.length === 0 ? (
                    <FilesEmpty>No files in this project.</FilesEmpty>
                  ) : (
                    <>
                      <FilesEmpty>
                        This segment predates durable file tracking — showing all files from the project as a
                        fallback.
                      </FilesEmpty>
                      <FilesList>
                        {projectFiles.map((item, idx) => {
                          const f = item.file;
                          const name = f?.name ?? `File ${idx + 1}`;
                          const ext = f?.extension ?? extensionOf(name) ?? '';
                          return (
                            <FileRow
                              key={f?.id ?? `${name}-${idx}`}
                              type="button"
                              title={name}
                              onClick={() => {
                                /* no viewer wired yet — row is visually clickable */
                              }}
                            >
                              <FileRowIcon>
                                <FileIcon extension={ext || 'txt'} size={28} />
                              </FileRowIcon>
                              <FileRowDetails>
                                <FileRowName>{basenameOf(name)}</FileRowName>
                                <FileRowSubtitle>Project file</FileRowSubtitle>
                              </FileRowDetails>
                            </FileRow>
                          );
                        })}
                      </FilesList>
                    </>
                  )}
                </FilesSection>
              )}
            </FilesPanel>
          )}
          {activeTab === 'message_generator' && (
            <MessageGeneratorTabContainer>
              <MessageGeneratorScreen
                initialSegmentationSessionId={
                  ((segmentDbRecord?.metadata as Record<string, unknown> | undefined)?._sessionId as
                    | string
                    | undefined) ?? runForFiles?.sessionId
                }
                initialSegmentId={segmentDbRecord?.identifier as string | undefined}
              />
            </MessageGeneratorTabContainer>
          )}
        </Wrap>
      </LeftModule>

      {/* Sidebar chat — visible on all tabs except Message Generator */}
      {chatOpen && activeTab !== 'message_generator' && (
        <RightModule>
          <SidebarArea>
            <ChatInterfaceNative
              onClose={() => setChatOpen(false)}
              segmentIdentifier={segmentDbRecord?.identifier as string | undefined}
              segmentationRunId={segmentationRunId}
              segmentProfile={
                seg
                  ? {
                      name: seg.name,
                      size: seg.size,
                      pct: seg.pct,
                      profile_narrative: seg.profile_narrative,
                      commercial_action: seg.commercial_action,
                      archetype_distribution: seg.archetype_distribution,
                      momentum_distribution: seg.momentum_distribution,
                      distinguishing_features: seg.distinguishing_features,
                      top_molecules: seg.top_molecules,
                    }
                  : null
              }
            />
          </SidebarArea>
        </RightModule>
      )}

      {/* Lightbulb toggle button — visible when chat is closed, but not on Message Generator tab */}
      {!chatOpen && activeTab !== 'message_generator' && (
        <ChatToggleBtn onClick={() => setChatOpen(true)} title="Discover Insights">
          <LightbulbIcon size={24} />
        </ChatToggleBtn>
      )}
    </Root>
  );
}
